// Mentions tab for the notification bell: GET /api/me/mentions, jump links,
// mark read. useMentions() reports `available:false` on 404/501 so the bell
// hides the tab entirely until the backend is live.
import React, { useCallback, useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import { socialFetch } from './socialApi';
import { createPoller, usePoller } from '../../hooks/sharedPoll';
import MentionText from './MentionText';
import { Avatar, BadgeIcons } from './socialUi';

// First page, polled once for the whole app (sharedPoll.js) and paused while the tab is hidden.
const mentionsPoller = createPoller(async () => {
  if (!localStorage.getItem('token')) return undefined;
  const r = await socialFetch('/api/me/mentions');
  if (r.ok && r.data && Array.isArray(r.data.mentions)) return { available: true, items: r.data.mentions, next: r.data.next_cursor || null };
  return r.status === 0 ? undefined : { available: false, items: [], next: null }; // network blip: keep what we had
}, 60000);

const NO_MENTIONS = { available: false, items: [], next: null };

export function useMentions() {
  const first = usePoller(mentionsPoller) || NO_MENTIONS;
  const [more, setMore] = useState({ items: [], next: undefined }); // pages loaded with "Load more"
  const [readIds, setReadIds] = useState(() => new Set());
  useEffect(() => { setMore({ items: [], next: undefined }); setReadIds(new Set()); }, [first]);

  const items = [...first.items, ...more.items].map((m) => (readIds.has(m.id) || readIds.has('all') ? { ...m, read: true } : m));
  const next = more.next === undefined ? first.next : more.next;

  const markRead = useCallback((ids) => {
    setReadIds((prev) => (ids === 'all' ? new Set(['all']) : new Set([...prev, ...ids])));
    return socialFetch('/api/me/mentions/read', { method: 'POST', body: ids === 'all' ? { all: true } : { ids } });
  }, []);

  const loadMore = useCallback(async () => {
    if (!next) return;
    const r = await socialFetch(`/api/me/mentions?cursor=${encodeURIComponent(next)}`);
    if (r.ok && r.data && Array.isArray(r.data.mentions)) {
      setMore((m) => ({ items: [...m.items, ...r.data.mentions], next: r.data.next_cursor || null }));
    }
  }, [next]);

  return { available: first.available, items, next, unread: items.filter((m) => !m.read).length,
           reload: mentionsPoller.refresh, markRead, loadMore };
}

const KIND = { chat: 'in chat', idea: 'on an idea', comment: 'in a comment', duel: 'in a duel' };

const ago = (iso) => {
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (!Number.isFinite(s)) return '';
  if (s < 60) return 'now';
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
};

/** Same-origin path for a mention url, or null. */
export const jumpPath = (url) => {
  if (!url) return null;
  try {
    const u = new URL(url, window.location.origin);
    return u.origin === window.location.origin ? u.pathname + u.search + u.hash : null;
  } catch { return null; }
};

export default function MentionsList({ mentions, onOpen, pal, emptyText = 'No mentions yet.' }) {
  if (!mentions.length) {
    return <Box sx={{ p: 2.5, textAlign: 'center', color: pal.emptyText, fontSize: '0.82rem' }}>{emptyText}</Box>;
  }
  return mentions.map((m) => (
    <Box key={m.id} role="button" tabIndex={0} onClick={() => onOpen(m)}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(m); } }}
      sx={{ display: 'flex', gap: 1.25, p: 1.5, cursor: 'pointer', borderBottom: `1px solid ${pal.border}`,
        bgcolor: m.read ? pal.readBg : pal.unreadBg, '&:hover': { bgcolor: m.read ? pal.readHover : pal.unreadHover },
        '&:focus-visible': { outline: '2px solid #0A84FF', outlineOffset: -2 } }}>
      <Avatar user={m.from} size={30} />
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: '0.8rem', color: pal.titleText }}>
          <Box component="b" sx={{ fontWeight: 700 }}>{m.from && (m.from.display_name || `@${m.from.handle}`)}</Box>
          <BadgeIcons badges={m.from && m.from.badges} size={12} />
          <Box component="span" sx={{ color: pal.bodyText }}>mentioned you {KIND[m.kind] || ''}</Box>
          <Box component="span" sx={{ ml: 'auto', color: pal.timeText, fontSize: '0.68rem', flexShrink: 0 }}>{ago(m.created_at)}</Box>
        </Box>
        <Box onClick={(e) => { if (e.target.closest('a')) e.stopPropagation(); }} sx={{ mt: '2px', fontSize: '0.8rem', color: pal.bodyText, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
          <MentionText text={m.text} linkify={false} sx={{ whiteSpace: 'normal' }} />
        </Box>
      </Box>
      {!m.read && <Box aria-label="Unread" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#0A84FF', mt: '6px', flexShrink: 0 }} />}
    </Box>
  ));
}
